import React from "react";
import { ReactElement } from "react-markdown/lib/react-markdown";
import {
  AankoopkostenBestaandeBouw,
  AankoopkostenNieuwbouw,
  Financieringsbehoefte,
  Financieringsoort,
  FinancieringsOpzetFinancieringskosten,
  FinancieringsopzetHoofdstuk,
  KostenAndereFinancieringsvormen,
  Overigekosten,
  SoortPand,
  VoetnootOptions,
} from "../../../../../.generated/rapportage/rapportagetypes";
import { Paragraph } from "../../../components/Paragraph";
import Table from "../../../components/Table";
import { Column } from "../../../components/BaseTable";
import Heading from "../../../components/Heading";
import { formatCurrency } from "../../../../formatters";
import "./financieringsopzet.scss";
import defaultTheme from "../../../../../styles/themes/default";

type FinancieringsopzetProps = {
  financieringsopzet: FinancieringsopzetHoofdstuk;
};

const voetnotenMap: Record<string, string> = {
  Notaristarieven: "p3040202",
  Aftrekbaar: "p3040203",
};

const volgnummerMap: Record<number, string> = {
  0: "⁽¹⁾",
  1: "⁽²⁾",
  2: "⁽³⁾",
};

const mapVolgnummer = (
  voetnoot: VoetnootOptions,
  voetnoten: VoetnootOptions[]
): string => {
  if (volgnummerMap[voetnoten.indexOf(voetnoot)] === undefined) {
    return "";
  }

  return volgnummerMap[voetnoten.indexOf(voetnoot)];
};
const getVoetnoten = (
  voetnotenToShow: VoetnootOptions[],
  allVoetnoten: VoetnootOptions[]
): string => {
  const ordered = voetnotenToShow
    .map((voetnoot) => allVoetnoten.indexOf(voetnoot))
    .sort();
  return ordered
    .map((index) => mapVolgnummer(allVoetnoten[index], allVoetnoten))
    .join("");
};
const soortPand = (
  financieringsopzet: FinancieringsopzetHoofdstuk
): ReactElement => {
  if (
    !financieringsopzet.soortPand ||
    financieringsopzet.soortPand.length === 0
  ) {
    return <></>;
  }

  const soortPand = financieringsopzet.soortPand;
  const columns: Column[] = [
    {
      header: "Onderpand",
      accessor: "soortOnderpand",
      align: "left",
      dataType: "default",
    },
  ];

  let marktwaardeHeader = "";

  if (
    soortPand.some((pand) => pand.marktwaarde != null && pand.marktwaarde > 0)
  ) {
    marktwaardeHeader = "Marktwaarde";
  }

  if (
    soortPand.some(
      (pand) =>
        pand.marktwaardeNaVerbouwing != null && pand.marktwaardeNaVerbouwing > 0
    )
  ) {
    marktwaardeHeader += ", marktwaarde na verbouwing";
  }

  if (marktwaardeHeader !== "") {
    columns.push({
      header: marktwaardeHeader,
      accessor: (row: SoortPand) =>
        (row.marktwaarde !== null && row.marktwaarde > 0
          ? formatCurrency(row.marktwaarde)
          : "") +
        (row.marktwaardeNaVerbouwing !== null && row.marktwaardeNaVerbouwing > 0
          ? ", " + formatCurrency(row.marktwaardeNaVerbouwing)
          : ""),
      align: "left",
      dataType: "default",
    });
  }

  if (soortPand.some((pand) => pand.wozWaarde !== null && pand.wozWaarde > 0)) {
    columns.push({
      header: "WOZ-waarde",
      accessor: "wozWaarde",
      align: "left",
      dataType: "currency",
    });
  }

  if (soortPand.some((pand) => pand.adres !== null)) {
    columns.push({
      header: "Adres",
      accessor: (row: SoortPand) =>
        row.adres
          ? `${row.adres.straat} ${row.adres.huisnummer}${
              row.adres.toevoeging ? row.adres.toevoeging : ""
            }, ${row.adres.postcode} ${row.adres.plaats}`
          : "",
      align: "left",
      dataType: "default",
    });
  }

  if (soortPand.some((pand) => pand.bouwjaar !== null)) {
    columns.push({
      header: "Bouwjaar",
      accessor: "bouwjaar",
      align: "left",
      dataType: "default",
    });
  }

  if (soortPand.some((pand) => pand.energielabel !== null)) {
    columns.push({
      header: "Energielabel",
      accessor: "energielabel",
      align: "left",
      dataType: "default",
    });
  }

  return (
    <Table
      columns={columns}
      data={soortPand}
      id="soortpand-table"
      filterNullAnd0Columns={false}
      rotate
    />
  );
};

const addAankoopkostenNieuwbouw = (
  columns: Column[],
  aankoopkostenNieuwbouw: AankoopkostenNieuwbouw,
  voetnoten: VoetnootOptions[]
): void => {
  if (
    aankoopkostenNieuwbouw?.koopsomWoning !== 0 &&
    aankoopkostenNieuwbouw?.koopsomWoning !== null
  ) {
    columns.push({
      header:
        "Koopsom woning " +
        (aankoopkostenNieuwbouw.kostenKoper ? "k.k." : "v.o.n."),
      accessor: "koopsomWoning",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw.grondprijs !== 0 &&
    aankoopkostenNieuwbouw.grondprijs !== null
  ) {
    columns.push({
      header: "Grondprijs",
      accessor: "grondprijs",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw.meerwerk !== 0 &&
    aankoopkostenNieuwbouw.meerwerk !== null
  ) {
    columns.push({
      header: `Meerwerk`,
      accessor: "meerwerk",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw.bouwrenteVoorAankoop !== 0 &&
    aankoopkostenNieuwbouw.bouwrenteVoorAankoop !== null
  ) {
    columns.push({
      header: "Bouwrente over de periode vóór de aankoop",
      accessor: "bouwrenteVoorAankoop",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw.bouwrenteNaAankoop !== 0 &&
    aankoopkostenNieuwbouw.bouwrenteNaAankoop !== null
  ) {
    columns.push({
      header: `Bouwrente over de periode na de aankoop ${mapVolgnummer(
        VoetnootOptions.Aftrekbaar,
        voetnoten
      )}`,
      accessor: "bouwrenteNaAankoop",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw.renteverlies !== 0 &&
    aankoopkostenNieuwbouw.renteverlies !== null
  ) {
    columns.push({
      header: `Renteverlies tijdens de bouw ${mapVolgnummer(
        VoetnootOptions.Aftrekbaar,
        voetnoten
      )}`,
      accessor: "renteverlies",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw.afkoopsomErfpacht !== 0 &&
    aankoopkostenNieuwbouw.afkoopsomErfpacht !== null
  ) {
    columns.push({
      header: "Afkoopsom Erfpacht",
      accessor: "afkoopsomErfpacht",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw.bankgarantiekosten !== 0 &&
    aankoopkostenNieuwbouw.bankgarantiekosten !== null
  ) {
    columns.push({
      header: "Bankgarantiekosten",
      accessor: "bankgarantiekosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkostenNieuwbouw?.totaalAankoopkosten !== 0 &&
    aankoopkostenNieuwbouw?.totaalAankoopkosten !== null
  ) {
    columns.push({
      header: "Totaal aankoopkosten",
      accessor: (row: any) => [row.totaalAankoopkosten, "+"],
      dataType: "currency",
      align: "left",
      className: "sum-cell bold",
    });
  }
};

const addAankoopkostenBestaandeBouw = (
  columns: Column[],
  aankoopkosten: AankoopkostenBestaandeBouw,
  voetnoten: VoetnootOptions[]
): void => {
  if (
    aankoopkosten?.koopsomWoning !== 0 &&
    aankoopkosten?.koopsomWoning !== null
  ) {
    columns.push({
      header:
        "Koopsom woning " + (aankoopkosten.kostenKoper ? "k.k." : "v.o.n."),
      accessor: "koopsomWoning",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkosten?.koopsomRoerendeZaken !== 0 &&
    aankoopkosten?.koopsomRoerendeZaken !== null
  ) {
    columns.push({
      header: "Koopsom roerende zaken",
      accessor: "koopsomRoerendeZaken",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkosten?.overdrachtsbelasting !== 0 &&
    aankoopkosten?.overdrachtsbelasting !== null
  ) {
    columns.push({
      header: "Overdrachtsbelasting",
      accessor: "overdrachtsbelasting",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkosten?.makelaarscourtage !== 0 &&
    aankoopkosten?.makelaarscourtage !== null
  ) {
    columns.push({
      header: "Makelaarscourtage",
      accessor: "makelaarscourtage",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkosten?.leveringsakte !== 0 &&
    aankoopkosten?.leveringsakte !== null
  ) {
    columns.push({
      header: `Leveringsakte ${getVoetnoten(
        [VoetnootOptions.Notaristarieven],
        voetnoten
      )}`,
      accessor: "leveringsakte",
      align: "left",
      dataType: "currency",
    });
  }
  if (
    aankoopkosten.bankgarantiekosten !== 0 &&
    aankoopkosten.bankgarantiekosten !== null
  ) {
    columns.push({
      header: "Bankgarantiekosten",
      accessor: "bankgarantiekosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkosten?.verbouwingsKosten !== 0 &&
    aankoopkosten?.verbouwingsKosten !== null
  ) {
    columns.push({
      header: `Verbouwing/verbetering van de woning`,
      accessor: "verbouwingsKosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    aankoopkosten?.totaalAankoopkosten !== 0 &&
    aankoopkosten?.totaalAankoopkosten !== null
  ) {
    columns.push({
      header: "Totaal aankoopkosten",
      accessor: (row: any) => [row.totaalAankoopkosten, "+"],
      dataType: "currency",
      align: "left",
      className: "sum-cell bold",
    });
  }
};

// Add columns for kostenAndereFinancieringsvormen
const addKostenAndereFinancieringsvormenColumns = (
  columns: Column[],
  kosten: KostenAndereFinancieringsvormen,
  voetnoten: VoetnootOptions[]
): void => {
  if (
    kosten.afTeLossenBestaandeHypotheken !== 0 &&
    kosten.afTeLossenBestaandeHypotheken !== null
  ) {
    columns.push({
      header: "Af te lossen bestaande hypotheken",
      accessor: "afTeLossenBestaandeHypotheken",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    kosten.overdrachtsbelasting !== 0 &&
    kosten.overdrachtsbelasting !== null
  ) {
    columns.push({
      header: "Overdrachtsbelasting",
      accessor: "overdrachtsbelasting",
      align: "left",
      dataType: "currency",
    });
  }

  if (kosten.leveringsakte !== 0 && kosten.leveringsakte !== null) {
    columns.push({
      header: "Leveringsakte",
      accessor: "leveringsakte",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    kosten.verbouwVerbeteringWoning !== 0 &&
    kosten.verbouwVerbeteringWoning !== null
  ) {
    columns.push({
      header: `Verbouwing/verbetering van de woning `,
      accessor: "verbouwVerbeteringWoning",
      align: "left",
      dataType: "currency",
    });
  }

  if (kosten.afkoopErfpacht !== 0 && kosten.afkoopErfpacht !== null) {
    columns.push({
      header: "Afkoopsom Erfpacht",
      accessor: "afkoopErfpacht",
      align: "left",
      dataType: "currency",
    });
  }

  if (kosten.vergoedingPartner !== 0 && kosten.vergoedingPartner !== null) {
    columns.push({
      header: "Vergoeding partner",
      accessor: "vergoedingPartner",
      align: "left",
      dataType: "currency",
    });
  }

  if (kosten?.totaalKosten !== 0 && kosten?.totaalKosten !== null) {
    columns.push({
      header: "Totaal kosten",
      accessor: (row: any) => [row.totaalKosten, "+"],
      align: "right",
      dataType: "currency",
      className: "sum-cell",
    });
  }
};

// Add columns for financieringskosten
const addFinancieringsKostenColumns = (
  columns: Column[],
  financieringskosten: FinancieringsOpzetFinancieringskosten,
  voetnoten: VoetnootOptions[]
): void => {
  columns.push({
    header: "Financieringskosten ",
    accessor: () => [" "],
    align: "left",
  });

  if (
    financieringskosten?.kostenHypotheek !== 0 &&
    financieringskosten?.kostenHypotheek !== null
  ) {
    columns.push({
      header: `Kosten hypotheekakte ${getVoetnoten(
        [VoetnootOptions.Notaristarieven, VoetnootOptions.Aftrekbaar],
        voetnoten
      )}`,
      accessor: "kostenHypotheek",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.arbeidskostenNotarisBedrag !== 0 &&
    financieringskosten?.arbeidskostenNotarisBedrag !== null
  ) {
    columns.push({
      header: `Arbeidskosten notaris ${getVoetnoten(
        [VoetnootOptions.Notaristarieven, VoetnootOptions.Aftrekbaar],
        voetnoten
      )}`,
      accessor: "arbeidskostenNotarisBedrag",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.kostenTaxatie !== 0 &&
    financieringskosten?.kostenTaxatie !== null
  ) {
    columns.push({
      header: `Taxatiekosten ${getVoetnoten(
        [VoetnootOptions.Notaristarieven, VoetnootOptions.Aftrekbaar],
        voetnoten
      )}`,
      accessor: "kostenTaxatie",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.nhgKosten !== 0 &&
    financieringskosten?.nhgKosten !== null
  ) {
    columns.push({
      header: `NHG-kosten ${mapVolgnummer(
        VoetnootOptions.Aftrekbaar,
        voetnoten
      )}`,
      accessor: "nhgKosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.advieskosten !== 0 &&
    financieringskosten?.advieskosten !== null
  ) {
    columns.push({
      header: `Advieskosten ${mapVolgnummer(
        VoetnootOptions.Aftrekbaar,
        voetnoten
      )}`,
      accessor: "advieskosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.bemiddelingskosten !== 0 &&
    financieringskosten?.bemiddelingskosten !== null
  ) {
    columns.push({
      header: `Bemiddelingsvergoeding t.b.v. geldlening ${mapVolgnummer(
        VoetnootOptions.Aftrekbaar,
        voetnoten
      )}`,
      accessor: "bemiddelingskosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.dossiervergoeding !== 0 &&
    financieringskosten?.dossiervergoeding !== null
  ) {
    columns.push({
      header: `Dossiervergoeding t.b.v. geldlening ${mapVolgnummer(
        VoetnootOptions.Aftrekbaar,
        voetnoten
      )}`,
      accessor: "dossiervergoeding",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.afsluitkostenStarterslening !== 0 &&
    financieringskosten?.afsluitkostenStarterslening !== null
  ) {
    columns.push({
      header: "Afsluitkosten SVn Starterslening",
      accessor: "afsluitkostenStarterslening",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.afsluitkostenOverbruggingskrediet !== 0 &&
    financieringskosten?.afsluitkostenOverbruggingskrediet !== null
  ) {
    columns.push({
      header: "Afsluitkosten overbruggingskrediet",
      accessor: "afsluitkostenOverbruggingskrediet",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.overigebemiddelingsvergoeding !== 0 &&
    financieringskosten?.overigebemiddelingsvergoeding !== null
  ) {
    columns.push({
      header: "Overige bemiddelingsvergoedingen",
      accessor: "overigebemiddelingsvergoeding",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.bereidstellingskosten !== 0 &&
    financieringskosten?.bereidstellingskosten !== null
  ) {
    columns.push({
      header: `Bereidstellingskosten ${mapVolgnummer(
        VoetnootOptions.Aftrekbaar,
        voetnoten
      )}`,
      accessor: "bereidstellingskosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.boeterente !== 0 &&
    financieringskosten?.boeterente !== null
  ) {
    columns.push({
      header: "Boeterente",
      accessor: "boeterente",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    financieringskosten?.totaalFinancieringskosten !== 0 &&
    financieringskosten?.totaalFinancieringskosten !== null
  ) {
    columns.push({
      header: "Totaal financieringskosten",
      accessor: (row: any) => [row.totaalFinancieringskosten, "+"],
      dataType: "currency",
      align: "left",
      className: "sum-cell bold",
    });
  }
};

function addOverigeKostenColumns(
  columns: Column<any>[],
  overigeKosten: Overigekosten,
  voetnoten: VoetnootOptions[]
): void {
  columns.push({
    header: "Overige kosten",
    accessor: () => [" "],
    align: "left",
  });

  if (
    overigeKosten.gewenstConsumptief !== 0 &&
    overigeKosten.gewenstConsumptief !== null
  ) {
    columns.push({
      header: "Gewenst consumptief te besteden bedrag",
      accessor: "gewenstConsumptief",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.overigeKosten1 !== 0 &&
    overigeKosten.overigeKosten1 !== null
  ) {
    columns.push({
      header: overigeKosten.overigeKosten1Omschrijving ?? "",
      accessor: "overigeKosten1",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.overigeKosten2 !== 0 &&
    overigeKosten.overigeKosten2 !== null
  ) {
    columns.push({
      header: overigeKosten.overigeKosten2Omschrijving ?? "",
      accessor: "overigeKosten2",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.overigeKosten3 !== 0 &&
    overigeKosten.overigeKosten3 !== null
  ) {
    columns.push({
      header: overigeKosten.overigeKosten3Omschrijving ?? "",
      accessor: "overigeKosten3",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.royementskosten !== 0 &&
    overigeKosten.royementskosten !== null
  ) {
    columns.push({
      header: "Royementskosten",
      accessor: "royementskosten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.bouwkundigeKeuring !== 0 &&
    overigeKosten.bouwkundigeKeuring !== null
  ) {
    columns.push({
      header: `Bouwkundige keuring ${getVoetnoten(
        [VoetnootOptions.Aftrekbaar],
        voetnoten
      )}`,
      accessor: "bouwkundigeKeuring",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.totaleRestschuld !== 0 &&
    overigeKosten.totaleRestschuld !== null
  ) {
    columns.push({
      header: "Totale restschuld",
      accessor: "totaleRestschuld",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.kostenOmzettenLeningdeel !== 0 &&
    overigeKosten.kostenOmzettenLeningdeel !== null
  ) {
    columns.push({
      header: "Kosten omzetting leningdeel",
      accessor: "kostenOmzettenLeningdeel",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten.inTeLossenKredieten !== 0 &&
    overigeKosten.inTeLossenKredieten !== null
  ) {
    columns.push({
      header: "In te lossen kredieten",
      accessor: "inTeLossenKredieten",
      align: "left",
      dataType: "currency",
    });
  }

  if (
    overigeKosten?.totaalOverigeKosten !== 0 &&
    overigeKosten?.totaalOverigeKosten !== null
  ) {
    columns.push({
      header: "Totaal overige kosten",
      accessor: (row: any) => [row.totaalOverigeKosten, "+"],
      dataType: "currency",
      align: "left",
      className: "sum-cell bold",
    });
  }

  columns.push({
    header: "",
    accessor: () => "",
    align: "right",
    className: "empty-row",
  });
}

function addFinancieringsbehoefteColumns(
  columns: Column<any>[],
  financieringsbehoefte: Financieringsbehoefte
): void {
  columns.push({
    header: "Financieringsbehoefte",
    accessor: () => "",
  });

  if (
    financieringsbehoefte.totaleFinancieringsbehoefte !== 0 &&
    financieringsbehoefte.totaleFinancieringsbehoefte !== null
  ) {
    columns.push({
      header: "Totale financieringsbehoefte",
      accessor: (row: any) => [row.totaleFinancieringsbehoefte, "+"],
      dataType: "currency",
      className: "sum-cell",
    });

    if (
      financieringsbehoefte.inbrengEigenGeld !== 0 &&
      financieringsbehoefte.inbrengEigenGeld !== null
    ) {
      columns.push({
        header: "Inbreng eigen middelen",
        accessor: (row: any) => [row.inbrengEigenGeld, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.inbrengVerwachteOverwaarde !== 0 &&
      financieringsbehoefte.inbrengVerwachteOverwaarde !== null
    ) {
      columns.push({
        header: "Inbreng (verwachte) overwaarde",
        accessor: (row: any) => [row.inbrengVerwachteOverwaarde, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.extraAflossing !== 0 &&
      financieringsbehoefte.extraAflossing !== null
    ) {
      columns.push({
        header: "Extra aflossing",
        accessor: (row: any) => [row.extraAflossing, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.kortlopendeLening !== 0 &&
      financieringsbehoefte.kortlopendeLening !== null
    ) {
      columns.push({
        header: "Kortlopende lening/kredit",
        accessor: (row: any) => [row.kortlopendeLening, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.eigenGeldOverig1 !== 0 &&
      financieringsbehoefte.eigenGeldOverig1 !== null
    ) {
      columns.push({
        header: financieringsbehoefte.eigenGeldOverigOmschrijving1 ?? "",
        accessor: (row: any) => [row.eigenGeldOverig1, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.eigenGeldOverig2 !== 0 &&
      financieringsbehoefte.eigenGeldOverig2 !== null
    ) {
      columns.push({
        header: financieringsbehoefte.eigenGeldOverigOmschrijving2 ?? "",
        accessor: (row: any) => [row.eigenGeldOverig2, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.eigengeldInbrengBedrag !== 0 &&
      financieringsbehoefte.eigengeldInbrengBedrag !== null
    ) {
      columns.push({
        header: "Inbreng opgebouwde waarde",
        accessor: (row: any) => [row.eigengeldInbrengBedrag, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.eigenGeldInbrengOverig1Bedrag !== 0 &&
      financieringsbehoefte.eigenGeldInbrengOverig1Bedrag !== null
    ) {
      columns.push({
        header: financieringsbehoefte.eigenGeldInbrengOverig1Omschrijving ?? "",
        accessor: (row: any) => [row.eigenGeldInbrengOverig1Bedrag, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.overbruggingskrediet !== 0 &&
      financieringsbehoefte.overbruggingskrediet !== null
    ) {
      columns.push({
        header: "Overbruggingskrediet",
        accessor: (row: any) => [row.overbruggingskrediet, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.doorlopendeLeningdelen !== 0 &&
      financieringsbehoefte.doorlopendeLeningdelen !== null
    ) {
      columns.push({
        header: "Doorlopende huidige hypotheken",
        accessor: (row: any) => [row.doorlopendeLeningdelen, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.overigeFinanciering1 !== 0 &&
      financieringsbehoefte.overigeFinanciering1 !== null
    ) {
      columns.push({
        header: financieringsbehoefte.overigeFinanciering1Omschrijving ?? "",
        accessor: (row: any) => [row.overigeFinanciering1, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.overigeFinanciering2 !== 0 &&
      financieringsbehoefte.overigeFinanciering2 !== null
    ) {
      columns.push({
        header: financieringsbehoefte.overigeFinanciering2Omschrijving ?? "",
        accessor: (row: any) => [row.overigeFinanciering2, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.sVnStarterslening !== 0 &&
      financieringsbehoefte.sVnStarterslening !== null
    ) {
      columns.push({
        header: "SVn Starterslening",
        accessor: (row: any) => [row.sVnStarterslening, "-/-"],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.gewenstHypotheekbedrag !== 0 &&
      financieringsbehoefte.gewenstHypotheekbedrag !== null
    ) {
      columns.push({
        header: "Gewenst hypotheekbedrag",
        accessor: (row: any) => [row.gewenstHypotheekbedrag, "+"],
        dataType: "currency",
        align: "left",
        className: "sum-cell bold",
      });
    }

    if (
      financieringsbehoefte.eigenwoningschuldBedrag !== 0 &&
      financieringsbehoefte.eigenwoningschuldBedrag !== null
    ) {
      columns.push({
        header: "Eigenwoningschuldbedrag",
        accessor: (row: any) => [row.eigenwoningschuldBedrag, ""],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }

    if (
      financieringsbehoefte.box3 !== 0 &&
      financieringsbehoefte.box3 !== null
    ) {
      columns.push({
        header: "Box 3",
        accessor: (row: any) => [row.box3, ""],
        dataType: "currency",
        align: "left",
        className: "sum-cell",
      });
    }
  }
}

// Create financieringsbehoefteTable function
const financieringsbehoefteTable = (
  hoofdstuk: FinancieringsopzetHoofdstuk
): ReactElement => {
  const financieringsbehoefte =
    hoofdstuk.financieringsopzet?.financieringsbehoefte;

  if (!financieringsbehoefte) {
    return <></>;
  }

  const columns: Column[] = [];

  addFinancieringsbehoefteColumns(columns, financieringsbehoefte);

  const data = [financieringsbehoefte];

  return (
    <Table
      rotate
      autoAlignCurrency
      columns={columns}
      data={data}
      id="financieringsbehoefte-table"
      className="labels-right break-avoid financieringsbehoefte-table"
    />
  );
};

// Create financieringsKostenTable function
const financieringsKostenTable = (
  hoofdstuk: FinancieringsopzetHoofdstuk
): ReactElement => {
  const financieringskosten = hoofdstuk.financieringsopzet?.financieringskosten;

  if (!financieringskosten) {
    return <></>;
  }

  const columns: Column[] = [];
  addFinancieringsKostenColumns(
    columns,
    financieringskosten,
    hoofdstuk.financieringsopzet?.voetnoten ?? []
  );

  const dataRow = {
    kostenHypotheek: financieringskosten?.kostenHypotheek,
    arbeidskostenNotarisBedrag: financieringskosten?.arbeidskostenNotarisBedrag,
    kostenTaxatie: financieringskosten?.kostenTaxatie,
    nhgKosten: financieringskosten?.nhgKosten,
    advieskosten: financieringskosten?.advieskosten,
    bemiddelingskosten: financieringskosten?.bemiddelingskosten,
    dossiervergoeding: financieringskosten?.dossiervergoeding,
    afsluitkostenStarterslening:
      financieringskosten?.afsluitkostenStarterslening,
    afsluitkostenOverbruggingskrediet:
      financieringskosten?.afsluitkostenOverbruggingskrediet,
    overigebemiddelingsvergoeding:
      financieringskosten?.overigebemiddelingsvergoeding,
    bereidstellingskosten: financieringskosten?.bereidstellingskosten,
    boeterente: financieringskosten?.boeterente,
    totaalFinancieringskosten: financieringskosten?.totaalFinancieringskosten,
  };

  const data = [dataRow];

  return (
    <Table
      rotate
      autoAlignCurrency
      columns={columns}
      data={data}
      id="financieringskosten-table"
      className="labels-right financieringskosten-table financieringsopzet-table"
    />
  );
};

const verbouwingsspecificatieTable = (
  hoofdstuk: FinancieringsopzetHoofdstuk
): ReactElement => {
  const dataRow = {
    verbouwingsKosten:
      hoofdstuk.financieringsopzet?.aankoopkostenBestaandeBouw
        ?.verbouwingsKosten ??
      hoofdstuk.financieringsopzet?.aankoopkostenNieuwbouw?.meerwerk ??
      hoofdstuk.financieringsopzet?.kostenAndereFinancieringsvormen
        ?.verbouwVerbeteringWoning,
    energieBespaarBudget:
      hoofdstuk.financieringsopzet?.aankoopkostenBestaandeBouw
        ?.energieBespaarBudget ??
      hoofdstuk.financieringsopzet?.aankoopkostenNieuwbouw
        ?.energieBespaarBudget ??
      hoofdstuk.financieringsopzet?.kostenAndereFinancieringsvormen
        ?.energieBespaarBudget,
    energieBesparendeVoorzieningen:
      hoofdstuk.financieringsopzet?.aankoopkostenBestaandeBouw
        ?.energieBesparendeVoorzieningen ??
      hoofdstuk.financieringsopzet?.aankoopkostenNieuwbouw
        ?.energieBesparendeVoorzieningen ??
      hoofdstuk.financieringsopzet?.kostenAndereFinancieringsvormen
        ?.energieBesparendeVoorzieningen,
  };

  const columns: Column[] = [
    {
      header: "Verbouwingsspecificatie",
      accessor: () => "",
    },
    {
      header: "Verbouwing/verbetering van de woning",
      accessor: (row: AankoopkostenBestaandeBouw) => [
        formatCurrency(row.verbouwingsKosten),
        " ",
      ],
    },
    {
      header: "Waarvan energiebesparingsbudget",
      accessor: "energieBespaarBudget",
      dataType: "currency",
    },
    {
      header: "Waarvan energiebesparende voorzieningen",
      accessor: "energieBesparendeVoorzieningen",
      dataType: "currency",
    },
  ];

  if (dataRow.energieBespaarBudget || dataRow.energieBesparendeVoorzieningen) {
    return (
      <Table
        rotate
        autoAlignCurrency
        columns={columns}
        data={[dataRow]}
        id="verbouwingsspecificatie-table"
        className="labels-right financieringsopzet-table"
      />
    );
  } else return <></>;
};

// Create financieringsopzetTable function
const financieringsopzetTable = (
  hoofdstuk: FinancieringsopzetHoofdstuk
): ReactElement => {
  const isAankoop =
    hoofdstuk.financieringsopzet?.financieringsoort ===
      Financieringsoort.AankoopNieuwbouw ||
    hoofdstuk.financieringsopzet?.financieringsoort ===
      Financieringsoort.AankoopBestaandeBouw;

  const columns: Column[] = [
    {
      header: "Financieringsopzet",
      accessor: () => "",
      align: "left",
    },
    {
      header: isAankoop ? "Aankoopkosten" : "Kosten",
      accessor: () => [" "],
    },
  ];

  const aankoopkostenNieuwbouw =
    hoofdstuk.financieringsopzet?.aankoopkostenNieuwbouw;

  if (aankoopkostenNieuwbouw != null) {
    addAankoopkostenNieuwbouw(
      columns,
      aankoopkostenNieuwbouw,
      hoofdstuk.financieringsopzet?.voetnoten ?? []
    );
  }

  const aankoopkostenBestaandeBouw =
    hoofdstuk.financieringsopzet?.aankoopkostenBestaandeBouw;

  if (aankoopkostenBestaandeBouw != null) {
    addAankoopkostenBestaandeBouw(
      columns,
      aankoopkostenBestaandeBouw,
      hoofdstuk.financieringsopzet?.voetnoten ?? []
    );
  }

  const kostenAndereFinancieringsvormen =
    hoofdstuk.financieringsopzet?.kostenAndereFinancieringsvormen;

  if (kostenAndereFinancieringsvormen != null) {
    addKostenAndereFinancieringsvormenColumns(
      columns,
      kostenAndereFinancieringsvormen,
      hoofdstuk.financieringsopzet?.voetnoten ?? []
    );
  }

  // Create data object with all fields from aankoopkosten
  const dataRow = {
    koopsomWoning:
      aankoopkostenNieuwbouw?.koopsomWoning ??
      aankoopkostenBestaandeBouw?.koopsomWoning,
    grondprijs: aankoopkostenNieuwbouw?.grondprijs,
    meerwerk: aankoopkostenNieuwbouw?.meerwerk,
    bouwrenteVoorAankoop: aankoopkostenNieuwbouw?.bouwrenteVoorAankoop,
    bouwrenteNaAankoop: aankoopkostenNieuwbouw?.bouwrenteNaAankoop,
    renteverlies: aankoopkostenNieuwbouw?.renteverlies,
    afkoopsomErfpacht: aankoopkostenNieuwbouw?.afkoopsomErfpacht,
    totaalAankoopkosten:
      aankoopkostenNieuwbouw?.totaalAankoopkosten ??
      aankoopkostenBestaandeBouw?.totaalAankoopkosten,
    koopsomRoerendeZaken: aankoopkostenBestaandeBouw?.koopsomRoerendeZaken,
    overdrachtsbelasting:
      aankoopkostenBestaandeBouw?.overdrachtsbelasting ??
      kostenAndereFinancieringsvormen?.overdrachtsbelasting,
    makelaarscourtage: aankoopkostenBestaandeBouw?.makelaarscourtage,
    leveringsakte:
      aankoopkostenBestaandeBouw?.leveringsakte ??
      kostenAndereFinancieringsvormen?.leveringsakte,
    verbouwingsKosten: aankoopkostenBestaandeBouw?.verbouwingsKosten,
    bankgarantiekosten:
      aankoopkostenBestaandeBouw?.bankgarantiekosten ??
      aankoopkostenNieuwbouw?.bankgarantiekosten,
    afTeLossenBestaandeHypotheken:
      kostenAndereFinancieringsvormen?.afTeLossenBestaandeHypotheken,
    verbouwVerbeteringWoning:
      kostenAndereFinancieringsvormen?.verbouwVerbeteringWoning,
    afkoopErfpacht: kostenAndereFinancieringsvormen?.afkoopErfpacht,
    vergoedingPartner: kostenAndereFinancieringsvormen?.vergoedingPartner,
    totaalKosten: kostenAndereFinancieringsvormen?.totaalKosten,
  };

  const data = [dataRow];

  return (
    <div>
      <Table
        rotate
        autoAlignCurrency
        columns={columns}
        data={data}
        id="financieringsopzet-table"
        className="labels-right financieringsopzet-table"
      />
      {verbouwingsspecificatieTable(hoofdstuk)}
    </div>
  );
};

// Create overigeKostenTable function
const overigeKostenTable = (
  hoofdstuk: FinancieringsopzetHoofdstuk
): ReactElement => {
  const overigeKosten = hoofdstuk.financieringsopzet?.overigekosten;

  if (!overigeKosten) {
    return <></>;
  }

  const columns: Column[] = [];
  addOverigeKostenColumns(
    columns,
    overigeKosten,
    hoofdstuk.financieringsopzet?.voetnoten ?? []
  );

  const dataRow = {
    gewenstConsumptief: overigeKosten?.gewenstConsumptief,
    overigeKosten1Omschrijving: overigeKosten?.overigeKosten1Omschrijving,
    overigeKosten2Omschrijving: overigeKosten?.overigeKosten2Omschrijving,
    overigeKosten3Omschrijving: overigeKosten?.overigeKosten3Omschrijving,
    overigeKosten1: overigeKosten?.overigeKosten1,
    overigeKosten2: overigeKosten?.overigeKosten2,
    overigeKosten3: overigeKosten?.overigeKosten3,
    bouwkundigeKeuring: overigeKosten?.bouwkundigeKeuring,
    royementskosten: overigeKosten?.royementskosten,
    totaleRestschuld: overigeKosten?.totaleRestschuld,
    kostenOmzettenLeningdeel: overigeKosten?.kostenOmzettenLeningdeel,
    inTeLossenKredieten: overigeKosten?.inTeLossenKredieten,
    totaalOverigeKosten: overigeKosten?.totaalOverigeKosten,
  };

  const data = [dataRow];

  return (
    <Table
      rotate
      autoAlignCurrency
      columns={columns}
      data={data}
      id="overigekosten-table"
      className="labels-right break-avoid overigekosten-table financieringsopzet-table"
    />
  );
};

export const FinancieringsopzetChapter = ({
  financieringsopzet,
}: FinancieringsopzetProps): ReactElement => {
  const overigeKostenData =
    financieringsopzet.financieringsopzet?.overigekosten;
  const financieringsbehoefteData =
    financieringsopzet.financieringsopzet?.financieringsbehoefte;

  return (
    <>
      {financieringsopzet.titel && (
        <Heading text={`${financieringsopzet.titel}`} level={2}></Heading>
      )}

      {(financieringsopzet.financieringsopzet?.financieringsoort ===
        Financieringsoort.Oversluiten ||
        financieringsopzet.financieringsopzet?.financieringsoort ===
          Financieringsoort.Omzetting) && (
        <Paragraph className="chapter__paragraph">
          {financieringsopzet?.paragrafen?.p3040204?.inhoud ?? ""}
        </Paragraph>
      )}

      {!(
        financieringsopzet.financieringsopzet?.financieringsoort ===
          Financieringsoort.Oversluiten ||
        financieringsopzet.financieringsopzet?.financieringsoort ===
          Financieringsoort.Omzetting
      ) && (
        <Paragraph className="chapter__paragraph">
          {financieringsopzet?.paragrafen?.p3040201?.inhoud ?? ""}
        </Paragraph>
      )}

      {soortPand(financieringsopzet)}
      <br />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: defaultTheme.tableGap,
        }}
      >
        {financieringsopzetTable(financieringsopzet)}
        {financieringsKostenTable(financieringsopzet)}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: defaultTheme.tableGap,
        }}
      >
        {overigeKostenData ? (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              {overigeKostenData && overigeKostenTable(financieringsopzet)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {financieringsbehoefteData &&
                financieringsbehoefteTable(financieringsopzet)}
            </div>
          </>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              {financieringsbehoefteData &&
                financieringsbehoefteTable(financieringsopzet)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}></div>
          </>
        )}
      </div>
      <br />
      {financieringsopzet.financieringsopzet?.voetnoten &&
        financieringsopzet.financieringsopzet?.voetnoten?.length > 0 && (
          <Paragraph className="break-avoid">
            {financieringsopzet.financieringsopzet?.voetnoten?.map(
              (voetnoot) => {
                return voetnotenMap[voetnoot] ? (
                  <React.Fragment key={voetnoot}>
                    {
                      financieringsopzet?.paragrafen?.[voetnotenMap[voetnoot]]
                        ?.inhoud
                    }
                    <br />
                  </React.Fragment>
                ) : null;
              }
            )}
          </Paragraph>
        )}
    </>
  );
};
